import { Fragment } from "react/jsx-runtime";
import { Chapter, SlideLayout, TemplateType } from "../types";
import styled from "styled-components";
import { useAntTheme } from "hooks/useAntTheme";
import {
  Button,
  Checkbox,
  CheckboxChangeEvent,
  Divider,
  Flex,
  Popconfirm,
  Popover,
  Skeleton,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import { PerChapterTransitionSwitch } from "./common/PerChapterTransitionSwitch";
import { IntroClosingEnableSwitch } from "./common/IntroClosingEnableSwitch";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useTemplateEditorStore } from "../store/useTemplateEditorStore";
import { useTemplateData } from "../hooks/useTemplateData";
import { useSlideMutations } from "../hooks/useSlideMutations";
import {
  LAYOUT_PREVIEW_OUTLINE_WIDTH,
  STORYBOARD_THUMBNAILS_PADDING,
  STORYBOARD_THUMBNAILS_WIDTH,
} from "../constants";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { SlideTemplatePreview } from "./Template/SlideTemplatePreview";
import { TemplatePreview } from "./Template/TemplatePreview";
import { useTranslate } from "@refinedev/core";
import {
  getChapterHasTransition,
  getClosingEnabled,
  getIntroEnabled,
} from "../helpers";
import { SectionWithSlides } from "hooks/useStoryboardWithSlides/";
import type { AddableSlideLayout } from "../helpers/buildDefaultSlide";
import { getSlotsForSlideTypeFromTheme } from "../helpers/itemsHelper";
import { MainImageSelectModal } from "./MainImageSelectModal";
import { radius } from "@libertify/ui";

const ADDABLE_LAYOUTS: AddableSlideLayout[] = [
  SlideLayout.BULLET_POINT,
  SlideLayout.QUOTE,
  SlideLayout.FIGURE,
  SlideLayout.IMAGE,
  SlideLayout.DOUBLE_PARAGRAPH,
  SlideLayout.PARAGRAPH_IMAGE,
];

const I18N_KEY =
  "components.templates.TemplatesBoard.storyboardSlidesThumbnails";

const ScenePickerPopover = ({
  onPick,
  children,
}: {
  onPick: (layout: AddableSlideLayout) => void;
  children: ReactNode;
}) => {
  const t = useTranslate();
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottom"
      title={t(`${I18N_KEY}.addScenePickerTitle`)}
      content={
        <Flex vertical gap={4}>
          {ADDABLE_LAYOUTS.map((layout) => (
            <Button
              key={layout}
              type="text"
              style={{
                textAlign: "left",
                justifyContent: "flex-start",
              }}
              onClick={() => {
                setOpen(false);
                onPick(layout);
              }}
            >
              {t(`${I18N_KEY}.slideTypes.${layout}`)}
            </Button>
          ))}
        </Flex>
      }
    >
      {children}
    </Popover>
  );
};

export const StoryboardSlidesThumbnails = ({
  handleSlideCardClick,
  handleChapterCardClick,
  checkboxProps,
  filterSlideLayoutType,
}: {
  handleSlideCardClick?: (uuid: string) => void;
  handleChapterCardClick?: (chapter: Chapter) => void;
  checkboxProps?: {
    checkedScenes: string[];
    handleChapterCheckboxChange: (
      e: CheckboxChangeEvent,
      slideId: string
    ) => void;
  };
  showCheckbox?: boolean;
  checkedChapters?: string[];
  handleChapterCheckboxChange?: (
    e: CheckboxChangeEvent,
    slideId: string
  ) => void;
  filterSlideLayoutType?: SlideLayout;
}) => {
  const { theme } = useAntTheme();
  const t = useTranslate();

  const {
    templatesData: { hasTransition, isFlashProject },
    brandKitData: { intro, closing },
    storyboardSections,
    mediaGenerationSettingsData: {
      mediaAspectRatio,
      mediaGenerationSettings,
      setChapterTransitionVisibility,
    },
    projectTheme,
  } = useTemplateData();

  const {
    currentSlide,
    currentChapter,
    slideNameToTemplateIdMap,
    templates,
    setCurrentChapter,
  } = useTemplateEditorStore((state) => ({
    currentSlide: state.currentSlide,
    currentChapter: state.currentChapter,
    slideNameToTemplateIdMap: state.slideNameToTemplateIdMap,
    templates: state.templates,
    setCurrentChapter: state.setCurrentChapter,
  }));

  const { addSlide, removeSlide, pendingSlideUuid } = useSlideMutations();

  const [pendingPick, setPendingPick] = useState<{
    kind: "after" | "before";
    anchorUuid: string;
    layout: AddableSlideLayout;
  } | null>(null);

  const layoutHasMainImage = (layout: AddableSlideLayout) => {
    if (!projectTheme) return false;
    const slots = getSlotsForSlideTypeFromTheme(projectTheme, layout);
    return !!slots?.some(
      (slot) => "mainImageItem" in slot && slot.mainImageItem
    );
  };

  const handlePick = (
    kind: "after" | "before",
    anchorUuid: string,
    layout: AddableSlideLayout
  ) => {
    if (layoutHasMainImage(layout)) {
      setPendingPick({ kind, anchorUuid, layout });
      return;
    }
    void addSlide(anchorUuid, layout, kind);
  };

  const selectedThumbnailRef = useRef<HTMLDivElement>(null);

  const isCurrentIntro = currentChapter.type === TemplateType.INTRODUCTION;
  const isCurrentClosing = currentChapter.type === TemplateType.CLOSING;

  useEffect(() => {
    if (selectedThumbnailRef.current) {
      selectedThumbnailRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentSlide]);

  const handleTransitionSwitchChange = (
    section: SectionWithSlides,
    checked: boolean,
    transitionTemplateId: string,
    currentChapterTemplateId: string | undefined
  ) => {
    // If the user is currently viewing this
    // chapter's transition slide, navigate to
    // the chapter so the canvas stops showing
    // the (now-hidden) slide.
    if (
      !checked &&
      currentSlide &&
      transitionTemplateId &&
      slideNameToTemplateIdMap[currentSlide] === transitionTemplateId &&
      section.slides.some((s) => s.uuid === currentSlide)
    ) {
      setCurrentChapter({
        templateId: currentChapterTemplateId,
        topic: section.topic,
        sectionId: section.id,
        type: TemplateType.CHAPTER,
      });
    }
    setChapterTransitionVisibility(section.id, checked).catch((error) =>
      console.error("Error toggling chapter transition", error)
    );
  };

  const introEnabled = getIntroEnabled(mediaGenerationSettings, !!intro);
  const closingEnabled = getClosingEnabled(mediaGenerationSettings, !!closing);
  const showIntroClosingSwitch = !checkboxProps;

  return (
    <Flex
      style={{
        borderRadius: `${radius.box}px ${radius.box}px 0 0`,
        height: "calc(100% - 24px)",
        marginBlockStart: 24,
        marginInlineStart: 24,
        overflowY: "hidden",
        paddingInline: 0,
        paddingBlockStart: STORYBOARD_THUMBNAILS_PADDING,
        backgroundColor: theme.colorBgContainer,
        zIndex: 50,
      }}
    >
      <StyledScrollableFlex vertical gap={8}>
        {intro && (
          <IntroClosingThumbnail
            isCurrent={isCurrentIntro}
            handleCardClick={handleChapterCardClick}
            mediaAspectRatio={mediaAspectRatio}
            selectedThumbnailRef={selectedThumbnailRef}
            enabled={introEnabled}
            showSwitch={showIntroClosingSwitch}
            chapter={{
              templateId:
                mediaGenerationSettings?.introduction_template_id ?? undefined,
              topic: "Introduction",
              sectionId: "",
              type: TemplateType.INTRODUCTION,
            }}
          />
        )}
        {storyboardSections.map((section, chapterIndex) => {
          const currentChapterSettings = mediaGenerationSettings?.chapters.find(
            (chapter) => chapter.chapter_id === section.id
          );
          const transitionTemplateId =
            currentChapterSettings?.transition_template_id ?? undefined;
          const chapterHasTransition = getChapterHasTransition(
            currentChapterSettings,
            hasTransition
          );

          const hideSection =
            filterSlideLayoutType &&
            section.slides.every(
              (slide) => slide.slide_type !== filterSlideLayoutType
            );
          if (hideSection) return;

          // Hide the per-chapter switch inside the Apply Settings drawer:
          // that flow uses checkboxes for bulk selection.
          const showPerChapterSwitch = !checkboxProps;

          return (
            <Flex key={section.id} vertical gap={16}>
              <StickyChapterLabel>
                <Typography.Text
                  strong
                  style={{ fontSize: "14px", textTransform: "uppercase" }}
                >
                  {t(
                    "components.templates.TemplatesBoard.storyboardSlidesThumbnails.chapter",
                    {
                      number: chapterIndex + 1,
                    }
                  )}
                </Typography.Text>
              </StickyChapterLabel>
              {(() => {
                const nonTransitionCount = section.slides.filter(
                  (s) =>
                    slideNameToTemplateIdMap[s.uuid] !== transitionTemplateId
                ).length;
                const firstNonTitleUuid = section.slides.find(
                  (s) =>
                    slideNameToTemplateIdMap[s.uuid] !== transitionTemplateId
                )?.uuid;
                let visibleSceneNumber = 0;
                return section.slides.map((slide) => {
                  const template = templates.get(slide.uuid);
                  if (!template) return;

                  const slideTemplateId = slideNameToTemplateIdMap[slide.uuid];
                  if (!slideTemplateId) return;

                  const layoutType = template.slideLayoutType;

                  const hideSlide =
                    !layoutType ||
                    (filterSlideLayoutType &&
                      filterSlideLayoutType !== layoutType);
                  if (hideSlide) return;

                  const isTransitionSlide =
                    slideTemplateId === transitionTemplateId;
                  // In drawer mode, disabled transitions are hidden entirely
                  // (can't be bulk-applied to). In editor mode, the row stays
                  // so the user can flip the switch back on.
                  if (
                    isTransitionSlide &&
                    !chapterHasTransition &&
                    checkboxProps
                  )
                    return;

                  const isCurrentSlide =
                    !!currentSlide && currentSlide === slide.uuid;

                  const canRemove = isFlashProject && nonTransitionCount > 1;
                  const canAdd = isFlashProject;

                  const templatePreview = (
                    <SlideTemplatePreview
                      ref={isCurrentSlide ? selectedThumbnailRef : null}
                      border={
                        isCurrentSlide
                          ? { borderColor: theme.colorPrimary }
                          : { borderColor: "#00000033", borderWidth: 1 }
                      }
                      opacity={1}
                      templateSize={getThumbnailSize(mediaAspectRatio)}
                      onCardClick={() => {
                        handleSlideCardClick?.(slide.uuid);
                      }}
                      slide={slide}
                      template={template}
                    />
                  );

                  if (!isTransitionSlide) visibleSceneNumber += 1;
                  const sceneLabel = isTransitionSlide
                    ? t(
                        "components.templates.TemplatesBoard.storyboardSlidesThumbnails.transitionScene"
                      )
                    : t(
                        "components.templates.TemplatesBoard.storyboardSlidesThumbnails.scene",
                        { number: visibleSceneNumber }
                      );

                  const showTransitionSwitch =
                    isTransitionSlide && showPerChapterSwitch;
                  const showSlidePreview =
                    !isTransitionSlide || chapterHasTransition;

                  const isFirstNonTitle =
                    !isTransitionSlide && slide.uuid === firstNonTitleUuid;
                  const isPending = slide.uuid === pendingSlideUuid;

                  if (isPending) {
                    return (
                      <LayoutWrapper key={slide.uuid}>
                        <Skeleton.Node
                          active
                          style={getThumbnailSize(mediaAspectRatio)}
                        />
                      </LayoutWrapper>
                    );
                  }

                  return (
                    <Fragment key={slide.uuid}>
                      {isFirstNonTitle && canAdd && (
                        <ScenePickerPopover
                          onPick={(layout) => {
                            handlePick("before", slide.uuid, layout);
                          }}
                        >
                          <Button
                            type="dashed"
                            size="small"
                            icon={<PlusOutlined />}
                            style={{ alignSelf: "center" }}
                          >
                            {t(`${I18N_KEY}.addScene`)}
                          </Button>
                        </ScenePickerPopover>
                      )}
                      <LayoutWrapper>
                        <Flex
                          justify={
                            checkboxProps ||
                            showTransitionSwitch ||
                            (!isTransitionSlide && canRemove)
                              ? "space-between"
                              : "center"
                          }
                          align="center"
                          style={{ width: "100%" }}
                        >
                          <Typography.Text
                            style={{
                              fontSize: "12px",
                              textTransform: "uppercase",
                            }}
                          >
                            {sceneLabel}
                          </Typography.Text>
                          <Flex gap={4} align="center">
                            {showTransitionSwitch && (
                              <PerChapterTransitionSwitch
                                chapterNumber={chapterIndex + 1}
                                labelAs="chapter"
                                checked={chapterHasTransition}
                                onChange={(checked) =>
                                  handleTransitionSwitchChange(
                                    section,
                                    checked,
                                    transitionTemplateId,
                                    currentChapterSettings?.chapter_template_id ??
                                      undefined
                                  )
                                }
                              />
                            )}
                            {checkboxProps && (
                              <ChapterCheckbox
                                disabled={isCurrentSlide}
                                checked={
                                  isCurrentSlide ||
                                  checkboxProps.checkedScenes.includes(
                                    slide.uuid
                                  )
                                }
                                onChange={(e) =>
                                  checkboxProps.handleChapterCheckboxChange(
                                    e,
                                    slide.uuid
                                  )
                                }
                              />
                            )}
                            {!isTransitionSlide && canRemove && (
                              <Popconfirm
                                title={t(`${I18N_KEY}.removeSceneConfirm`)}
                                okText={t(`${I18N_KEY}.removeSceneConfirmYes`)}
                                cancelText={t(
                                  `${I18N_KEY}.removeSceneConfirmNo`
                                )}
                                onConfirm={() => void removeSlide(slide.uuid)}
                                placement="bottomRight"
                              >
                                <Tooltip title={t(`${I18N_KEY}.removeScene`)}>
                                  <DeleteSceneButton
                                    type="text"
                                    aria-label={t(`${I18N_KEY}.removeScene`)}
                                    icon={<DeleteOutlined />}
                                  />
                                </Tooltip>
                              </Popconfirm>
                            )}
                          </Flex>
                        </Flex>
                        {showSlidePreview && templatePreview}
                        {!isTransitionSlide && canAdd && (
                          <ScenePickerPopover
                            onPick={(layout) => {
                              handlePick("after", slide.uuid, layout);
                            }}
                          >
                            <Button
                              type="dashed"
                              size="small"
                              icon={<PlusOutlined />}
                            >
                              {t(`${I18N_KEY}.addScene`)}
                            </Button>
                          </ScenePickerPopover>
                        )}
                      </LayoutWrapper>
                    </Fragment>
                  );
                });
              })()}
              {chapterIndex < storyboardSections.length - 1 && (
                <Divider
                  style={{
                    marginBlock: "8px",
                    borderBlockEnd: `1px solid ${theme.colorBorder}`,
                    opacity: 1,
                  }}
                />
              )}
            </Flex>
          );
        })}
        {closing && (
          <IntroClosingThumbnail
            isCurrent={isCurrentClosing}
            handleCardClick={handleChapterCardClick}
            mediaAspectRatio={mediaAspectRatio}
            selectedThumbnailRef={selectedThumbnailRef}
            enabled={closingEnabled}
            showSwitch={showIntroClosingSwitch}
            chapter={{
              templateId:
                mediaGenerationSettings?.closing_template_id ?? undefined,
              topic: "Closing",
              sectionId: "",
              type: TemplateType.CLOSING,
            }}
          />
        )}
      </StyledScrollableFlex>
      <MainImageSelectModal
        open={!!pendingPick}
        onCancel={() => setPendingPick(null)}
        sectionId={
          storyboardSections.find((section) =>
            section.slides.some((slide) => slide.uuid === pendingPick?.anchorUuid)
          )?.id
        }
        onSelect={(assetId, path, mediaType, script) => {
          if (!pendingPick) return;
          const { kind, anchorUuid, layout } = pendingPick;
          setPendingPick(null);
          void addSlide(
            anchorUuid,
            layout,
            kind,
            { assetId, path, mediaType },
            script
          );
        }}
      />
    </Flex>
  );
};

export const getThumbnailSize = (aspectRatio: number | undefined) => ({
  width:
    STORYBOARD_THUMBNAILS_WIDTH -
    STORYBOARD_THUMBNAILS_PADDING * 2 -
    LAYOUT_PREVIEW_OUTLINE_WIDTH * 2,
  height: Math.round(
    (STORYBOARD_THUMBNAILS_WIDTH -
      STORYBOARD_THUMBNAILS_PADDING * 2 -
      LAYOUT_PREVIEW_OUTLINE_WIDTH * 2) /
      (aspectRatio ?? 9 / 16)
  ),
});

export const StyledScrollableFlex = styled(Flex)`
  padding-inline: ${STORYBOARD_THUMBNAILS_PADDING}px;
  padding-block-end: ${12}px;
  height: 100%;
  overflow-y: scroll;
  flex-shrink: 0;
  width: min-content;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colorBorder} transparent;

  /* Chrome, Safari, Edge */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colorBorder};
    border-radius: 3px;
    opacity: 0.5;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colorTextDescription};
    opacity: 0.7;
  }

  .ant-card-hoverable {
    border-color: #00000000;
  }
`;

export const LayoutWrapper = styled(Flex)`
  position: relative;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const DeleteSceneButton = styled(Button)`
  font-size: 16px;
  width: 28px;
  height: 28px;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.colorError};
  }
`;

export const StickyChapterLabel = styled.div`
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colorBgContainer};
  padding-block: 8px;
  z-index: 10;
  font-weight: 500;
  text-align: center;
  opacity: 0.9;
`;

export const ChapterCheckbox = ({
  loading = false,
  disabled = true,
  checked = false,
  onChange,
}: {
  loading?: boolean;
  disabled?: boolean;
  checked?: boolean;
  onChange?: (e: CheckboxChangeEvent) => void;
}) => {
  if (loading) return <Spin spinning={loading} size="small" />;
  return <Checkbox disabled={disabled} checked={checked} onChange={onChange} />;
};

const IntroClosingThumbnail = ({
  isCurrent,
  handleCardClick,
  mediaAspectRatio,
  selectedThumbnailRef,
  chapter,
  enabled,
  showSwitch,
}: {
  isCurrent: boolean;
  handleCardClick?: (chapter: Chapter) => void;
  mediaAspectRatio: number;
  selectedThumbnailRef: React.RefObject<HTMLDivElement>;
  chapter: Chapter;
  enabled: boolean;
  showSwitch: boolean;
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const isIntro = chapter.type === TemplateType.INTRODUCTION;
  return (
    <LayoutWrapper style={isIntro ? undefined : { marginBlockStart: 24 }}>
      <Flex justify="space-between" align="center" style={{ width: "96%" }}>
        <Typography.Text
          style={{
            fontSize: "12px",
            textTransform: "uppercase",
          }}
        >
          {t(
            isIntro
              ? "components.templates.TemplatesBoard.storyboardSlidesThumbnails.introScene"
              : "components.templates.TemplatesBoard.storyboardSlidesThumbnails.closingScene"
          )}
        </Typography.Text>
        {showSwitch && <IntroClosingEnableSwitch isIntro={isIntro} />}
      </Flex>
      {enabled && (
        <TemplatePreview
          ref={isCurrent ? selectedThumbnailRef : null}
          chapter={chapter}
          onCardClick={() => {
            handleCardClick?.(chapter);
          }}
          templateSize={getThumbnailSize(mediaAspectRatio)}
          border={{
            borderColor: isCurrent ? theme.colorPrimary : undefined,
          }}
          opacity={1}
        />
      )}
    </LayoutWrapper>
  );
};
